<template>
  <div class="tutorial-menu" v-if="show" @click="$emit('close')">
    <div class="tutorial-menu-content" @click.stop>
      <div class="tutorial-menu-header">
        <h3>{{ $t('tutorial.menu.title') }}</h3>
        <button class="close-btn" @click="$emit('close')">×</button>
      </div>

      <div class="tutorial-menu-body">
        <!-- 数据共享教程 -->
        <div class="tutorial-item">
          <div class="tutorial-item-info">
            <div class="tutorial-item-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="tutorial-item-text">
              <h4>{{ $t('tutorial.menu.receiveCodeTutorial') }}</h4>
              <p>{{ $t('tutorial.receiveCode.description') }}</p>
              <span
                class="tutorial-status"
                :class="{ completed: isReceiveCodeCompleted }"
              >
                {{ isReceiveCodeCompleted ? $t('tutorial.menu.completed') : $t('tutorial.menu.notStarted') }}
              </span>
            </div>
          </div>
          <button
            class="btn btn-primary btn-sm"
            @click="handleStartReceiveCodeTutorial"
          >
            {{ $t('tutorial.common.startTutorial') }}
          </button>
        </div>

        <!-- 充值教程 -->
        <div class="tutorial-item">
          <div class="tutorial-item-info">
            <div class="tutorial-item-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
              </svg>
            </div>
            <div class="tutorial-item-text">
              <h4>{{ $t('tutorial.menu.rechargeTutorial') }}</h4>
              <p>{{ $t('tutorial.recharge.description') }}</p>
              <span
                class="tutorial-status"
                :class="{ completed: isRechargeCompleted }"
              >
                {{ isRechargeCompleted ? $t('tutorial.menu.completed') : $t('tutorial.menu.notStarted') }}
              </span>
            </div>
          </div>
          <button
            class="btn btn-primary btn-sm"
            @click="handleStartRechargeTutorial"
          >
            {{ $t('tutorial.common.startTutorial') }}
          </button>
        </div>
      </div>

      <div class="tutorial-menu-footer">
        <button class="btn btn-outline btn-sm" @click="handleResetAll">
          {{ $t('tutorial.menu.resetAll') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGlobalTutorial } from '@/utils/useTutorial'
import { ElMessageBox } from 'element-plus'

export default {
  name: 'TutorialMenu',

  props: {
    show: {
      type: Boolean,
      default: false
    }
  },

  emits: ['close', 'start-tutorial'],

  setup(props, { emit }) {
    const { t: $t } = useI18n()
    const { checkCompleted, resetAll, TUTORIAL_TYPES } = useGlobalTutorial()

    // 检查教程完成状态
    const isReceiveCodeCompleted = computed(() => {
      return checkCompleted(TUTORIAL_TYPES.RECEIVE_CODE)
    })

    const isRechargeCompleted = computed(() => {
      return checkCompleted(TUTORIAL_TYPES.RECHARGE)
    })

    /**
     * 开始数据共享教程
     */
    function handleStartReceiveCodeTutorial() {
      emit('start-tutorial', TUTORIAL_TYPES.RECEIVE_CODE)
      emit('close')
    }

    /**
     * 开始充值教程
     */
    function handleStartRechargeTutorial() {
      emit('start-tutorial', TUTORIAL_TYPES.RECHARGE)
      emit('close')
    }

    /**
     * 重置所有教程
     */
    async function handleResetAll() {
      try {
        await ElMessageBox.confirm(
          $t('tutorial.menu.resetConfirm'),
          {
            confirmButtonText: $t('common.confirm'),
            cancelButtonText: $t('common.cancel'),
            type: 'warning'
          }
        )

        resetAll()
        emit('close')
      } catch (error) {
        // 用户取消
      }
    }

    return {
      isReceiveCodeCompleted,
      isRechargeCompleted,
      handleStartReceiveCodeTutorial,
      handleStartRechargeTutorial,
      handleResetAll
    }
  }
}
</script>

<style scoped>
.tutorial-menu {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.tutorial-menu-content {
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.tutorial-menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
}

.tutorial-menu-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #0f172a;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  color: #64748b;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: #f1f5f9;
}

.tutorial-menu-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.tutorial-item {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  transition: all 0.2s;
}

.tutorial-item:hover {
  border-color: #4461F2;
  box-shadow: 0 4px 12px rgba(68, 97, 242, 0.1);
}

.tutorial-item-info {
  display: flex;
  gap: 16px;
  flex: 1;
}

.tutorial-item-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background-color: rgba(68, 97, 242, 0.1);
  color: #4461F2;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tutorial-item-text {
  flex: 1;
}

.tutorial-item-text h4 {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
}

.tutorial-item-text p {
  margin: 0 0 8px 0;
  font-size: 14px;
  line-height: 1.5;
  color: #64748b;
}

.tutorial-status {
  display: inline-block;
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 12px;
  background-color: #f1f5f9;
  color: #64748b;
}

.tutorial-status.completed {
  background-color: rgba(34, 197, 94, 0.1);
  color: #16a34a;
}

.tutorial-menu-footer {
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: center;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .tutorial-menu-content {
    width: 95%;
    max-height: 90vh;
  }

  .tutorial-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .tutorial-item button {
    width: 100%;
  }
}
</style>
